import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import printerApi from '../../APIs/printer';
import { useRoot } from '../../context/RootProvider';
import {
  Button,
  PageHeader,
  Pagination,
  Table,
  Typography,
} from '../../designComponents';
import { TableDataType } from '../../designComponents/Table';
import {
  VIEW_PRINTER_JOBS_DETAILS_ROUTE,
  VIEW_PRINTER_JOBS_ROUTE,
} from '../../routes/routePaths';
import theme from '../../theme';
import { humanReadableDate, requestAPI } from '../../utils';
import {
  ClientJobContainerStyled,
  ClientJobStyled,
} from '../PrinterClient/style';

type ParamsType = {
  page: number;
  limit: number;
};

type PrinterJobType = {
  _id: string;
  clientId: {
    _id: string;
    emailAddress: string;
  };
  jobTitle: string;
  notes: string;
  pdf: any[];
  shippingAddress: string;
  additionalRequirement: any[];
  printerJobStatus: string;
  printerJobStatusChangedDate: any[];
  createdAt: string;
  updatedAt: string;
};

const PrinterJob = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
  });

  const { auth } = useRoot();

  const id = auth?._id;

  const fetchJobs = async () => {
    try {
      const response = await requestAPI(
        printerApi.getAllPrinterJobs({
          printerId: id || '',
        })
      );

      // Extract the actual data array from the nested response
      const jobsData = response?.data || [];
      const pagination = response?.data?.pagination || {};

      const data = jobsData.map((job: PrinterJobType) => {
        return {
          id: job._id,
          date: humanReadableDate(job.createdAt),
          pdfName: job.jobTitle,
          shippingAddress: job.shippingAddress,
          printQuantity: String(job.pdf?.length || 0),
          style: { printQuantity: { textAlign: 'center' } },
          clientName: (
            <div>
              <span>{job.clientId?._id || '-'}</span>
            </div>
          ),
          email: job.clientId?.emailAddress || '-',
          companyAddress: job.shippingAddress,
          actions: (
            <Button
              variant="secondary"
              onClick={() => navigate(`${VIEW_PRINTER_JOBS_ROUTE}/${job._id}`)}
              style={{
                backgroundColor: theme.colors.blueLight,
                border: `1px solid ${theme.colors.blueLight}`,
              }}
            >
              View Details
            </Button>
          ),
        };
      });

      setJobs(data);
      setTotalCount(pagination.totalData || 0);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (id && auth.role === 'CLIENT') {
      fetchJobs();
    }
  }, [id]);

  const tableData: TableDataType = {
    headings: [
      { key: 'date', label: 'Date' },
      { key: 'pdfName', label: 'PDF Name' },
      { key: 'printQuantity', label: 'Print Quantity' },
      { key: 'clientName', label: ' Client Name' },
      { key: 'email', label: 'Email' },
      { key: 'companyAddress', label: 'Company Address' },
      { key: 'actions', label: 'Actions' },
    ],
    body: jobs,
  };

  const handlePagination = (updatedParams: ParamsType) => {
    setParams(updatedParams);
  };

  const handleOnSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
  };

  return (
    <ClientJobContainerStyled>
      <PageHeader
        pageTitle={<Typography variant="title">Jobs</Typography>}
        handleOnSearchTextChange={handleOnSearchTextChange}
        search={search}
      />

      <ClientJobStyled>
        <div className="table-container">
          <Table tableData={tableData} maxWidthForCardView="800px" />
        </div>
        <Pagination
          activePage={params.page}
          handlePagination={handlePagination}
          limit={params.limit}
          totalCount={totalCount}
        />
      </ClientJobStyled>
    </ClientJobContainerStyled>
  );
};

export default PrinterJob;
